import React, { useEffect, useState } from "react";
import { Users, Plus, UnlockKeyhole, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { PageHero, Section, Button, Input, Select, Field, DataTable, Badge, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ role: "guest", must_change_password: true });
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [u, r] = await Promise.all([api.get("/users"), api.get("/permissions/roles")]);
      setUsers(u.data || []); setRoles(r.data || []);
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("User created");
      setShowForm(false); setForm({ role: "guest", must_change_password: true });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }
  async function unlock(id) {
    try { await api.post(`/users/${id}/unlock`); toast.success("Unlocked"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <PageHero
        eyebrow="Administration"
        title="User management"
        subtitle="Create Home360 accounts, assign roles and manage account lifecycle."
        moduleKind="dashboard"
        actions={<Button variant="primary" onClick={() => setShowForm(v => !v)} data-testid="users-add-btn"><Plus className="w-4 h-4" /> New user</Button>}
      />

      <Section className="relative overflow-hidden">
        <Pattern kind="dashboard" />
        <div className="relative">
          {showForm && (
            <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Full name"><Input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="user-form-name" /></Field>
              <Field label="Username"><Input value={form.username || ""} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required /></Field>
              <Field label="Email"><Input type="email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required /></Field>
              <Field label="Role">
                <Select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="user-form-role">
                  {roles.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
                </Select>
              </Field>
              <Field label="Temporary password" hint="User must change on first login (14+ chars, upper, lower, digit, special)">
                <Input type="text" value={form.password || ""} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required />
              </Field>
              <Field label="Phone"><Input value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} /></Field>
              <div className="md:col-span-3 flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" variant="primary" data-testid="user-form-submit">Create user</Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>
          ) : users.length === 0 ? (
            <EmptyState icon={Users} title="No users yet" description="Create the first Home360 user." />
          ) : (
            <DataTable
              testid="users-table"
              columns={[
                { header: "Name", cell: r => <span className="font-medium">{r.name || r.username}</span> },
                { header: "Username", key: "username" },
                { header: "Email", key: "email" },
                { header: "Role", cell: r => <Badge variant="primary">{r.role}</Badge> },
                { header: "Status", cell: r => (
                  <div className="flex gap-1">
                    <Badge variant={r.is_active ? "healthy" : "critical"}>{r.is_active ? "Active" : "Disabled"}</Badge>
                    {r.locked_until && <Badge variant="critical"><ShieldAlert className="w-3 h-3" /> Locked</Badge>}
                    {r.must_change_password && <Badge variant="pending">Must change PW</Badge>}
                  </div>
                )},
                { header: "Actions", cell: r => (
                  <div className="flex gap-2">
                    {r.locked_until && <Button size="sm" variant="ghost" onClick={() => unlock(r.id)}><UnlockKeyhole className="w-3.5 h-3.5" /> Unlock</Button>}
                  </div>
                )},
              ]}
              rows={users}
            />
          )}
        </div>
      </Section>
    </div>
  );
}
